'use client';

import { Box, HStack } from '@terra/ui-styled-system';
import { Text, Badge } from '@terra/ui-design-system';
import { GitCompareIcon } from '@terra/ui-icons';
import { useComparerStore } from '../../store/comparer-store';
import { ComparerEmptyState } from './ComparerEmptyState';
import { ComparerItemList } from './ComparerItemList';
import { ComparerToolbar } from './ComparerToolbar';
import { DiffViewer } from './DiffViewer';
import { PasteDialog } from './PasteDialog';
import { EmptyState } from '../EmptyState';
import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import type { DiffViewerHandle } from './DiffViewer';

export function ComparerContent() {
  const { items, selectedLeftId, selectedRightId, autoSelectItems } =
    useComparerStore();
  const [isPasteDialogOpen, setIsPasteDialogOpen] = useState(false);
  const diffContainerRef = useRef<HTMLDivElement>(null);
  const diffViewerRef = useRef<DiffViewerHandle>(null);

  // Auto-select items when items change
  useEffect(() => {
    if (items.length >= 2 && !selectedLeftId && !selectedRightId) {
      autoSelectItems();
    }
  }, [items.length, selectedLeftId, selectedRightId, autoSelectItems]);
  // Get selected items
  const leftItem = useMemo(() => {
    return items.find((item) => item.id === selectedLeftId);
  }, [items, selectedLeftId]);
  const rightItem = useMemo(() => {
    return items.find((item) => item.id === selectedRightId);
  }, [items, selectedRightId]);
  // Handlers for navigating diffs
  const handleGoToNextDiff = useCallback(() => {
    diffViewerRef.current?.goToNextDiff();
  }, []);
  const handleGoToPrevDiff = useCallback(() => {
    diffViewerRef.current?.goToPrevDiff();
  }, []);
  const handleSearch = useCallback(() => {
    diffViewerRef.current?.openSearch();
  }, []);
  const handleOpenPasteDialog = useCallback(() => {
    setIsPasteDialogOpen(true);
  }, []);
  const handleClosePasteDialog = useCallback(() => {
    setIsPasteDialogOpen(false);
  }, []);

  // Empty state - no items at all
  if (items.length === 0) {
    return (
      <>
        <ComparerEmptyState onOpenPasteDialog={handleOpenPasteDialog} />
        <PasteDialog
          isOpen={isPasteDialogOpen}
          onClose={handleClosePasteDialog}
        />
      </>
    );
  }

  // Show layout with sidebar
  return (
    <Box h="full" display="flex" flexDirection="row">
      {/* Left sidebar - Item list */}
      <ComparerItemList onPaste={handleOpenPasteDialog} />

      {/* Main content area */}
      <Box flex="1" display="flex" flexDirection="column" minW={0}>
        {/* Toolbar */}
        {leftItem && rightItem && (
          <ComparerToolbar
            onGoToNextDiff={handleGoToNextDiff}
            onGoToPrevDiff={handleGoToPrevDiff}
            onSearch={handleSearch}
          />
        )}

        {/* Diff viewer or selection prompt */}
        <Box ref={diffContainerRef} flex="1" overflow="hidden">
          {leftItem && rightItem ? (
            <DiffViewer
              ref={diffViewerRef}
              leftContent={leftItem.content}
              rightContent={rightItem.content}
              leftLabel={leftItem.label}
              rightLabel={rightItem.label}
            />
          ) : (
            <SelectionPrompt
              hasLeft={!!leftItem}
              hasRight={!!rightItem}
              itemCount={items.length}
            />
          )}
        </Box>

        {/* Stats bar */}
        {leftItem && rightItem && <DiffStatsBar />}
      </Box>

      {/* Paste dialog */}
      <PasteDialog
        isOpen={isPasteDialogOpen}
        onClose={handleClosePasteDialog}
      />
    </Box>
  );
}

// Selection prompt component
function SelectionPrompt({
  hasLeft,
  hasRight,
  itemCount,
}: {
  hasLeft: boolean;
  hasRight: boolean;
  itemCount: number;
}) {
  const title =
    itemCount === 1
      ? 'Add another item to compare'
      : !hasLeft && !hasRight
        ? 'Select two items to compare'
        : !hasLeft
          ? 'Select a left item to compare'
          : 'Select a right item to compare';

  return (
    <EmptyState
      icon={GitCompareIcon}
      title={title}
      description="Use the sidebar to select items for comparison"
    />
  );
}

// Stats bar component
function DiffStatsBar() {
  const { totalDiffs, settings } = useComparerStore();

  return (
    <Box p="2" borderTopWidth="1px" borderColor="border.subtle" bg="bg.subtle">
      <HStack justifyContent="space-between" alignItems="center">
        <HStack gap="2">
          <Text fontSize="xs" color="fg.muted">
            View: {settings.viewMode}
          </Text>
        </HStack>
        <HStack gap="2">
          {totalDiffs > 0 && (
            <Badge variant="fill">
              {totalDiffs} {totalDiffs === 1 ? 'difference' : 'differences'}
            </Badge>
          )}
        </HStack>
      </HStack>
    </Box>
  );
}
